/**
 * Le module « ecole »: on entre par ici.
 *
 * `genererSerie` fabrique une liste de questions a partir d'une GRAINE: meme
 * graine, memes options, meme serie, sur le client comme dans un test. C'est ce
 * qui permet de rejouer exactement la partie decrite dans un journal.
 *
 * `composerResultat` transforme les reponses en une structure plate,
 * serialisable, que le serveur n'a qu'a ranger et convertir en recompenses.
 */
import { Alea } from '../alea.ts'
import type { Activite, Brouillon } from './atelier.ts'
import { PROGRESSION } from './progression.ts'
import { corriger, etoiles, pointsPartie } from './notation.ts'
import type {
  Matiere,
  ModeEcole,
  Niveau,
  OptionsSerie,
  ProgresEcole,
  Question,
  ReponseDonnee,
  ResultatPartie,
  Serie,
  Sujet,
} from './types.ts'
import { progresVide, sujetInfo, sujetsDe } from './types.ts'

export * from './types.ts'
export * from './notation.ts'
export * from './calculatrice.ts'
export { PROGRESSION } from './progression.ts'
export { HOMOPHONES } from './francais.ts'

/** Longueur d'une serie par defaut. Dix questions: assez court pour un enfant. */
export const NOMBRE_DEFAUT = 10

/** Bornes de longueur acceptees, pour qu'un appel farfelu ne bloque rien. */
const NOMBRE_MIN = 1
const NOMBRE_MAX = 40

/**
 * Le temps accorde par question dans le defi chronometre.
 * Il MONTE avec le niveau: une question de 6e demande plus de reflexion qu'une
 * de 1re, et un chrono trop court n'apprend rien — il decourage.
 */
export function secondesParQuestion(niveau: Niveau): number {
  return 12 + 2 * niveau
}

function borner(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v
}

/**
 * Fabrique une serie de questions.
 *
 * Les sujets tournent a tour de role (question 1 au premier sujet, question 2
 * au deuxieme...), et a l'interieur d'un sujet les activites tournent aussi, a
 * partir d'un depart tire au sort. Sans cette rotation, dix questions tirees au
 * hasard donnaient regulierement six fois le meme genre d'exercice.
 */
/** Tirages de plus, au plus, pour eviter une question deja posee dans la serie. */
const ESSAIS_SANS_DOUBLON = 6

/**
 * Meme enonce ET memes reponses: « Quel mot est bien écrit ? » garde son
 * enonce d'une question a l'autre, ce sont ses choix qui changent.
 */
function dejaPosee(questions: readonly Question[], b: Brouillon): boolean {
  return questions.some(
    (q) =>
      q.enonce === b.enonce &&
      q.reponses.join('|') === b.reponses.join('|') &&
      [...(q.choix ?? [])].sort().join('|') === [...(b.choix ?? [])].sort().join('|'),
  )
}

export function genererSerie(o: OptionsSerie): Serie {
  const sujets = o.sujets.length > 0 ? o.sujets : sujetsDe('maths')
  const nombre = Math.round(borner(o.nombre, NOMBRE_MIN, NOMBRE_MAX))
  const options: OptionsSerie = { ...o, sujets, nombre }
  const alea = new Alea(o.graine >>> 0)

  const depart = new Map<Sujet, number>()
  for (const s of sujets) depart.set(s, alea.entier(0, 5))

  const questions: Question[] = []
  for (let i = 0; i < nombre; i++) {
    const sujet = sujets[i % sujets.length] as Sujet
    const liste = PROGRESSION[sujet][o.niveau]
    const tour = Math.floor(i / sujets.length)
    const activite = liste[((depart.get(sujet) ?? 0) + tour) % liste.length] as Activite
    /*
     * PAS DEUX FOIS LA MEME QUESTION DANS UNE SERIE. Deux gabarits par niveau et
     * de petites plages de nombres: « 4 kilomètres » revenait en question 1 et
     * en question 9 (vu au telephone le 13 octobre). On retire quelques fois;
     * une serie sans doublon ne consomme donc pas un tirage de plus.
     */
    let b = activite.faire(alea)
    for (let essai = 0; essai < ESSAIS_SANS_DOUBLON && dejaPosee(questions, b); essai++) {
      b = activite.faire(alea)
    }
    // Une reserve plus petite que la serie (quatre formes en 1re annee) finit
    // par se vider: la serie RACCOURCIT plutot que de reposer une question
    // (choix de JC, 13 octobre).
    if (dejaPosee(questions, b)) continue
    const question: Question = {
      id: `${sujet}-${o.niveau}-${i}`,
      sujet,
      niveau: o.niveau,
      activite: activite.id,
      enonce: b.enonce,
      consigne: b.consigne,
      saisie: b.saisie,
      reponses: b.reponses,
      poids: o.niveau * 100 + activite.rang * 10 + borner(Math.round(b.taille), 0, 9),
    }
    if (b.choix) question.choix = b.choix
    if (b.unite) question.unite = b.unite
    questions.push(question)
  }

  return { options, questions, secondes: o.chrono ? questions.length * secondesParQuestion(o.niveau) : 0 }
}

/** La matiere d'une serie: francais des qu'un sujet de francais s'y trouve. */
export function matiereDe(sujets: readonly Sujet[]): Matiere {
  return sujets.every((s) => sujetInfo(s).matiere === 'maths') ? 'maths' : 'francais'
}

/**
 * Assemble le resultat d'une partie.
 *
 * `reponses` ne contient que les questions REELLEMENT posees: le chronometre
 * peut couper la serie en son milieu, et compter fausses des questions que
 * l'enfant n'a jamais vues serait injuste.
 */
export function composerResultat(
  mode: ModeEcole,
  serie: Serie,
  reponses: readonly ReponseDonnee[],
  msTotal: number,
): ResultatPartie {
  const total = reponses.length
  let justes = 0
  let courante = 0
  let meilleure = 0
  for (const r of reponses) {
    if (r.juste) {
      justes++
      courante++
      if (courante > meilleure) meilleure = courante
    } else {
      courante = 0
    }
  }
  const pourcentage = total > 0 ? Math.round((justes / total) * 100) : 0
  const o = serie.options
  return {
    version: 1,
    mode,
    matiere: matiereDe(o.sujets),
    niveau: o.niveau,
    sujets: [...o.sujets],
    graine: o.graine,
    chrono: o.chrono,
    total,
    justes,
    meilleureSerie: meilleure,
    pourcentage,
    etoiles: etoiles(pourcentage),
    points: pointsPartie(o.niveau, justes, total, o.chrono),
    msTotal: Math.max(0, Math.round(msTotal)),
    reponses: [...reponses],
  }
}

/**
 * Range une partie dans les progres cumules d'un compte. Fonction PURE: elle
 * rend un nouvel objet, elle n'ecrit rien. C'est au serveur de le persister.
 */
export function fusionnerProgres(progres: ProgresEcole | null, r: ResultatPartie): ProgresEcole {
  const base = progres ?? progresVide()
  const meilleur = { ...base.meilleurParNiveau }
  const clef = String(r.niveau)
  meilleur[clef] = Math.max(meilleur[clef] ?? 0, r.pourcentage)

  const justesParSujet = { ...base.justesParSujet }
  const posesParSujet = { ...base.posesParSujet }
  for (const rep of r.reponses) {
    posesParSujet[rep.sujet] = (posesParSujet[rep.sujet] ?? 0) + 1
    if (rep.juste) justesParSujet[rep.sujet] = (justesParSujet[rep.sujet] ?? 0) + 1
  }

  return {
    version: 1,
    points: base.points + r.points,
    parties: base.parties + 1,
    questions: base.questions + r.total,
    justes: base.justes + r.justes,
    meilleurParNiveau: meilleur,
    justesParSujet,
    posesParSujet,
    pointsDepenses: base.pointsDepenses,
  }
}

/** Corrige une reponse et fabrique la ligne qui partira dans le resultat. */
export function noterReponse(q: Question, donnee: string, ms: number): ReponseDonnee {
  return {
    id: q.id,
    sujet: q.sujet,
    activite: q.activite,
    enonce: q.enonce,
    donnee,
    attendue: q.reponses[0] ?? '',
    juste: corriger(q, donnee),
    ms: Math.max(0, Math.round(ms)),
  }
}
